/**
 * SEO HEADING STRUCTURE - KpopStudyPageTemplate
 * ==============================================
 * 
 * TITLE: {seoTitle} | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Unique per page (passed via seoDescription prop)
 * 
 * H1 (1 tag):
 *   - "Study With {groupNameDisplay}" (hero section)
 * 
 * H2 (7 tags):
 *   - "Study with {groupNameDisplay}" (about section)
 *   - "The Rise of 'Study with {groupNameDisplay}'" (rise of section)
 *   - "CSW: The Ultimate Platform to Study with {groupNameDisplay}" (features section)
 *   - "Why CSW is Perfect for {fandomName}" (why perfect section)
 *   - "Connect with Like-Minded Fans" (community section)
 *   - "Watch {groupNameDisplay}" (YouTube videos section)
 *   - "Ready to Study with {groupNameDisplay}?" (CTA section)
 * 
 * H3 (5 tags):
 *   - "Official {groupNameDisplay} Resources" (social links)
 *   - Dynamic feature card titles (4 cards in features grid)
 */

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { KpopCategoryNav } from "@/components/KpopCategoryNav";
import { Music, Users, Heart, ArrowRight, Sparkles, Radio, Play, ExternalLink } from "lucide-react";

interface SocialLink {
  name: string;
  url: string;
}

interface YouTubeVideo {
  id: string;
  title: string;
}

interface KpopStudyPageTemplateProps {
  groupName: string;
  groupNameDisplay: string;
  fandomName: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
  heroDescription: string;
  aboutContent: React.ReactNode;
  riseOfContent: React.ReactNode;
  featureCards: Array<{
    title: string;
    desc: string;
  }>;
  whyPerfectDescription: string;
  communityDescription1: string;
  communityDescription2: string;
  socialLinks: SocialLink[];
  youtubeVideos: YouTubeVideo[];
  youtubeChannelUrl: string;
  youtubeChannelName: string;
  image1?: string;
  image2?: string;
  image3?: string;
}

export const KpopStudyPageTemplate = ({
  groupName,
  groupNameDisplay,
  fandomName,
  seoTitle,
  seoDescription,
  seoKeywords,
  canonicalUrl,
  heroDescription,
  aboutContent,
  riseOfContent,
  featureCards,
  whyPerfectDescription,
  communityDescription1,
  communityDescription2,
  socialLinks,
  youtubeVideos,
  youtubeChannelUrl,
  youtubeChannelName,
  image1,
  image2,
  image3,
}: KpopStudyPageTemplateProps) => {
  // Generate JSON-LD structured data for K-pop study pages
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": canonicalUrl,
    "keywords": seoKeywords,
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "CSW",
      "alternateName": "Chill Study Work",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "url": "https://csw.live",
      "description": `Free online study platform to study with ${groupNameDisplay}. Features study rooms, productivity tools, and K-pop themed study sessions.`,
      "featureList": [
        `Study with ${groupNameDisplay}`,
        "K-pop themed study rooms",
        "Virtual study sessions",
        "Study stream with video",
        "Pomodoro timer",
        "Task management",
        "Study with strangers",
        "Group study sessions"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "ratingCount": "3000",
        "bestRating": "5",
        "worstRating": "1"
      },
      "author": {
        "@type": "Organization",
        "name": "CSW",
        "url": "https://csw.live"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      },
      "sameAs": [
        "https://www.instagram.com/csw.live/",
        "https://www.youtube.com/@CSW.live.workspace"
      ]
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://app.csw.live",
      "name": "Start Studying Now"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CSW STUDY TOGETHER</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With{" "}
              <span className="text-gradient">{groupNameDisplay}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {heroDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start A Room Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Study with <span className="text-gradient">{groupNameDisplay}</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
                {aboutContent}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* The Rise of Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  The Rise of <span className="text-gradient">"Study with {groupNameDisplay}"</span>
                </h2>
                <div className="text-muted-foreground text-lg leading-relaxed">
                  {riseOfContent}
                </div>
              </div>
              {image1 ? (
                <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all">
                  <img 
                    src={image1} 
                    alt={`${groupNameDisplay} study session`} 
                    className="w-full h-full object-cover"
                  />
                </Card>
              ) : (
                <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <div className="text-center p-8">
                    <Music className="w-16 h-16 text-primary mx-auto mb-4" />
                    <p className="text-foreground font-medium">{groupNameDisplay} Study Atmosphere</p>
                  </div>
                </Card>
              )}
            </div>

            {/* Official Links Section */}
            <div className="mt-16 text-center space-y-6">
              <h3 className="font-display text-2xl font-semibold text-foreground">Official {groupNameDisplay} Resources</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {socialLinks.map((link) => (
                  <a 
                    key={link.name}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/50 hover:border-primary/50 hover:bg-primary/10 transition-all text-sm font-medium text-foreground"
                  >
                    {link.name}
                    <ExternalLink className="w-3 h-3 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                CSW: The Ultimate Platform to <span className="text-gradient">Study with {groupNameDisplay}</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                At CSW, we offer dedicated pages and study rooms where you can study with K-Pop idols, including {groupNameDisplay}.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: <Music className="w-7 h-7" />, ...featureCards[0] },
                { icon: <Users className="w-7 h-7" />, ...featureCards[1] },
                { icon: <Sparkles className="w-7 h-7" />, ...featureCards[2] },
                { icon: <Radio className="w-7 h-7" />, ...featureCards[3] },
              ].map((feature, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why CSW is Perfect */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Why CSW is Perfect for <span className="text-gradient">{fandomName}</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <p className="text-muted-foreground leading-relaxed text-lg text-center">
                {whyPerfectDescription}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Connect with Community */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {image2 ? (
                <Card className="overflow-hidden order-2 lg:order-1 border-border/50 hover:border-primary/30 transition-all">
                  <img 
                    src={image2} 
                    alt={`${groupNameDisplay} community`} 
                    className="w-full h-full object-cover"
                  />
                </Card>
              ) : (
                <Card className="overflow-hidden order-2 lg:order-1 border-border/50 hover:border-primary/30 transition-all aspect-video bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center">
                  <div className="text-center p-8">
                    <Heart className="w-16 h-16 text-primary mx-auto mb-4 fill-primary" />
                    <p className="text-foreground font-medium">{fandomName} Community</p>
                  </div>
                </Card>
              )}
              <div className="space-y-8 order-1 lg:order-2">
                <div className="flex items-center gap-2 mb-6">
                  <Heart className="w-8 h-8 text-primary fill-primary" />
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  Connect with <span className="text-gradient">Like-Minded Fans</span>
                </h2>
                <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                  <p>{communityDescription1}</p>
                  <p>{communityDescription2}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Watch <span className="text-gradient">{groupNameDisplay}</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Get motivated with {groupNameDisplay}'s music and performances while you study. Check out their official videos below.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {youtubeVideos.map((video, index) => (
                <Card 
                  key={video.id} 
                  className="overflow-hidden border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in bg-card/50 backdrop-blur-sm"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-medium text-foreground text-sm">{video.title}</p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <a 
                href={youtubeChannelUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                Visit {youtubeChannelName} Official YouTube Channel →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">{groupNameDisplay}?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of {fandomName} who are staying focused and motivated with {groupNameDisplay}. Start your study session today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Explore CSW Rooms <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <KpopCategoryNav currentGroup={groupName} />

      <Footer />
    </div>
  );
};

export default KpopStudyPageTemplate;
